import React, { useRef, useState, useEffect, useReducer } from "react";
import client, { makeGet } from "@culturehq/client";
import { Icon, classnames, Loader, PlainButton } from "@culturehq/components";

import noStories from "images/no-stories.png";

import { t, Trans } from "app/utils";
import LightboxStories from "components/common/gallery/LightboxStories";
import styles from "./story-slider.module.scss";

const MIN_STORIES_TO_SHARE_MESSAGE = 1;

const storyBadge = {
  alignItems: "center",
  borderRadius: "6px",
  bottom: "0",
  display: "flex",
  justifyContent: "center",
  left: "0",
  position: "absolute",
  right: "0",
  top: "0"
};

const backgroundEffect = {
  background: "linear-gradient(0deg, rgba(0,0,0,.3), rgba(0,0,0,.7))",
  borderRadius: "6px",
  height: "100%",
  padding: "10px",
  position: "relative"
};

const chqTmb = {
  backgroundPosition: "50%",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  borderRadius: "25px",
  display: "inline-block",
  height: "30px",
  marginRight: "10px",
  minWidth: "30px",
  width: "30px"
};

const creatorName = {
  fontSize: "1.2em",
  marginBottom: "0",
  overflow: "hidden",
  overflowWrap: "break-word",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  wordBreak: "break-all"
};

const creatorTitle = {
  margin: "0",
  overflow: "hidden",
  overflowWrap: "break-word",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  wordBreak: "break-all"
};

const creatorContainer = {
  alignItems: "center",
  color: "#fff",
  display: "flex",
  fontSize: ".8em",
  fontWeight: "300",
  overflow: "hidden"
};

const initialState = {
  appending: null,
  error: null,
  fetching: false,
  pagination: {},
  stories: [],
  currentUserAnswers: 0
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1 };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        stories: [...state.stories, ...action.response.stories]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        stories: action.response.stories,
        currentUserAnswers: action.response.currentUserAnswers
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    case "storyUpdate":
      return {
        ...state,
        stories: state.stories.map(story => {
          if (story.id === action.storyId) {
            return { ...story, ...action.updates };
          }
          return story;
        })
      };
    case "storyRemove":
      return {
        ...state,
        stories: state.stories.filter(story => (
          story.id !== action.storyId
        ))
      };
    default:
      return state;
  }
};

const getPromptStories = ({ isPublic, page = 0, storyQuestionId }) => {
  if (isPublic) return makeGet(`/stories/public/stories/${storyQuestionId}`, { page });

  return client.listPromptStories({ page: 0, storyQuestionId });
};

const useFetchStories = (dispatch, storyQuestionId, isPublic) => useEffect(
  () => {
    let cancelled = false;
    dispatch({ type: "fetchStart" });

    getPromptStories({ isPublic, page: 0, storyQuestionId })
      .then(response => {
        if (!cancelled) {
          dispatch({ type: "fetchSuccess", response });
        }
      })
      .catch(error => {
        if (!cancelled) {
          dispatch({ type: "fetchError", error });
        }
      });

    return () => {
      cancelled = true;
    };
  },
  [dispatch, isPublic, storyQuestionId]
);

const useAppendStories = (dispatch, appending, storyQuestionId, isPublic) => useEffect(
  () => {
    if (appending) {
      let cancelled = false;

      getPromptStories({ isPublic, page: appending, storyQuestionId })
        .then(response => {
          if (!cancelled) {
            dispatch({ type: "appendSuccess", response });
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "appendError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  },
  [dispatch, appending, storyQuestionId, isPublic]
);

const getSlideLayout = (
  index,
  containerRef,
  sliderRef,
  stories
) => {
  const STORY_WIDTH = 220;
  let percent = index * (100 / (stories.length + 1));
  const left = index !== 0;
  let right = index !== stories.length;

  const container = containerRef.current;
  const slider = sliderRef.current;
  const sliderWidth = STORY_WIDTH * (stories.length + 1);

  if (!(container instanceof HTMLDivElement) || !(slider instanceof HTMLDivElement)) {
    return { percent, left, right };
  }

  const pixels = (percent * sliderWidth) / 100;
  const correction = pixels - (slider.clientWidth - container.clientWidth);

  if (sliderWidth <= container.clientWidth) {
    right = false;
  }

  if (correction > 0) {
    percent -= (correction / sliderWidth) * 100;
    right = false;
  }

  return { percent, left, right };
};

const StoryPromptSlider = ({
  isPublic,
  storyQuestion,
  onDeleteStory,
  onUpdateStory,
  onUserAnswers
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeStory, setActiveStory] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showArrow, setShowArrow] = useState(true);

  useFetchStories(dispatch, storyQuestion?.id, isPublic);
  useAppendStories(dispatch, state.appending, storyQuestion?.id, isPublic);

  const [index, setIndex] = useState(0);
  const onNext = () => setIndex(value => value + 1);
  const onPrev = () => setIndex(value => value - 1);

  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const slideLayout = getSlideLayout(index, containerRef, sliderRef, state.stories);

  useEffect(() => {
    const handleResize = () => {
      const STORY_WIDTH = 200;
      const MARGIN_BETWEEN_STORIES = 15;
      const DIV_WIDTH = containerRef.current.offsetWidth;

      const STORIES_PLACE = (state.stories.length * STORY_WIDTH)
      + ((state.stories.length - 1) * MARGIN_BETWEEN_STORIES);

      if (state.stories.length >= 2) {
        if (STORIES_PLACE > DIV_WIDTH) {
          setShowArrow(true);
        } else {
          setShowArrow(false);
        }
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [state.stories.length]);

  useEffect(
    () => {
      onUserAnswers(state.currentUserAnswers);
    }, [state.currentUserAnswers, onUserAnswers]
  );

  useEffect(
    () => {
      if (!slideLayout.right && state.stories.length !== 0
        && state.pagination.currentPage !== state.pagination.totalPages) {
        dispatch({ type: "appendStart" });
      }
    },
    [slideLayout.right, state.pagination, state.stories]
  );

  const handleOnDelete = id => {
    setModalIsOpen(false);
    dispatch({ type: "storyRemove", storyId: id });
    if (onDeleteStory) {
      onDeleteStory(id);
    }
  };

  const handleOnUpdate = story => {
    dispatch({ type: "storyUpdate", storyId: story.id, updates: story });
    setActiveStory(story);
    if (onUpdateStory) {
      onUpdateStory(story);
    }
  };

  const handleThumbnailClick = goToIndex => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = state.stories.length - 1;
    }

    if (goToIndex > state.stories.length - 1) {
      target = 0;
    }

    setActiveStory(state.stories[target]);
    setModalIsOpen(true);
  };

  const maxCardWidth = () => {
    if (state.stories.length === 1) {
      return "750px";
    }

    return "100%";
  };

  const backgroundImage = story => {
    if (state.stories.length >= 2) {
      if (story.media.thumbnail?.endsWith("200")) {
        return story.media.smallUrl || story.media.thumbnail.replace("200", "400");
      }
      return story.media.smallUrl || story.media.thumbnail || story.media.url;
    }

    return story.media.fullUrl || story.media.thumbnail || story.media.url;
  };

  const handleClose = () => {
    setModalIsOpen(false);
  };

  return (
    <div className="Responses-Stories" style={{ position: "relative" }}>
      {activeStory && (
        <LightboxStories
          modalIsOpen={modalIsOpen}
          activeStory={activeStory}
          stories={state.stories}
          onClose={handleClose}
          onDelete={handleOnDelete}
          onStoryUpdate={handleOnUpdate}
          onStoryChange={handleThumbnailClick}
        />
      )}
      <div ref={containerRef} className={classnames(styles.smallerContainer, "no-scrollbar", "Slider")}>
        <div
          ref={sliderRef}
          className={classnames("Slider", styles.sliderLeft)}
          style={{ transform: `translateX(-${slideLayout.percent}%)` }}
        >
          {state.stories.map((story, index) => (
            <PlainButton
              aria-labelledby={story.id}
              key={story.id}
              className={styles.smallerCard}
              style={{ backgroundImage: `url(${backgroundImage(story)})`, width: "200px", maxWidth: maxCardWidth() }}
              onClick={() => handleThumbnailClick(index)}
            >
              <div id={story.id} style={{ display: "none" }}>{`${story.creator.name}'s story about ${story.question.question}`}</div>
              <div style={{ ...backgroundEffect, height: "100%" }}>
                <div style={creatorContainer}>
                  <div style={{ ...chqTmb, backgroundImage: `url(${story.creator.avatar.thumbUrl})` }} />
                  <div style={{ maxWidth: "80%" }}>
                    <p style={creatorName}>{story.creator.name}</p>
                    <p style={creatorTitle}>{story.creator.title}</p>
                  </div>
                </div>
                {story.media.mediaType === "video" && (
                  <span style={storyBadge}>
                    <svg aria-hidden="true" role="presentation" width="22px" height="22px" viewBox="0 0 264 264">
                      <path transform="translate(0 0)" style={{ fill: "#FFFFFF" }} d="M238.163,115.57l-68.127-39.741c-15.201-8.899-40.064-23.393-55.296-32.256L44.115,3.831 C28.919-5.067,13.974,2.07,13.974,19.698v224c0,17.567,14.945,24.735,30.147,15.872l69.376-39.741 c15.232-8.863,40.735-23.357,55.936-32.256l68.449-39.741C253.047,138.933,253.334,124.433,238.163,115.57z" />
                    </svg>
                  </span>
                )}
              </div>
            </PlainButton>
          ))}
          {state.stories.length > 0 && state.stories.length <= MIN_STORIES_TO_SHARE_MESSAGE && (
            <div className={classnames(styles.emptyContainerDescription, "Stories__Empty")}>
              <img className="Stories__Empty--image" src={noStories} alt={t("Empty Stories")} />
              <div>
                <p className="Stories__Empty--paragraph"><Trans>Get your colleagues to join this trend.</Trans></p>
                <p className="Stories__Empty--paragraph"><Trans>Use the links on the left!</Trans></p>
              </div>
            </div>
          )}
          {state.stories.length === 0 && (
            <div className={classnames(styles.emptyContainerDescription, "Stories__Empty")}>
              {state.fetching ? <Loader loading /> : (
                <>
                  <img className="Stories__Empty--image" src={noStories} alt={t("Empty Stories")} />
                  <div>
                    <p className="Stories__Empty--paragraph"><Trans>No stories have been added to this prompt yet.</Trans></p>
                    <p className="Stories__Empty--paragraph"><Trans>Be the first!</Trans></p>
                  </div>
                </>
              )}
            </div>
          )}
          {state.appending && (
            <Loader loading />
          )}
        </div>
      </div>
      {showArrow && (
        <>
          <button
            aria-label={t("Previous")}
            type="button"
            className={classnames(styles.direction, styles.left)}
            onClick={onPrev}
            disabled={!slideLayout.left}
          >
            <Icon icon="chevron-left" />
          </button>
          <button
            aria-label={t("Next")}
            type="button"
            className={classnames(styles.direction, styles.right)}
            onClick={onNext}
            disabled={!slideLayout.right}
          >
            <Icon icon="chevron-right" />
          </button>
        </>
      )}
    </div>
  );
};

export default StoryPromptSlider;
